import { ChangeEvent, useRef, useState } from "react";
import { stdObject } from "../../../data/types/stdObject";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import NOPIC from "../../../assets/nopic.svg";
import { colors } from "../../../data/colors";
import { useTranslation } from "react-i18next";
import BasicButton from "../../BasicButton";
import { useMainContext } from "../../../context/MainContext";
import { mainConfig } from "../../../data/main";
import ImageEditorModal from "./ImageEditor/ImageEditorModal";

function MiniLabel({ title, text }: { title: string; text: string }) {
  return <div style={{ paddingTop: 5 }}>{`${title}: ${text}`}</div>;
}

export default function ImagesBox({ data = {} }: { data?: stdObject }) {
  const { t } = useTranslation();
  const { addUpdateImage } = useMainContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const getImageSrc = (e: stdObject) =>
    e.BASE
      ? e.BASE
      : e.FORIMG !== ""
        ? `${mainConfig.url}/images/ART/small/${data.value.code}${e.CODIMG > 0 ? "_" + e.CODIMG : ""}.${e.FORIMG}`
        : NOPIC;

  // Editor should use the best available resolution: try large, then
  // medium, then fall back to small (or the freshly-uploaded BASE preview).
  const getEditorImageSources = (e: stdObject) => {
    if (e.BASE) return [e.BASE];
    const suffix = e.CODIMG > 0 ? "_" + e.CODIMG : "";
    return ["large", "medium", "small"].map(
      (size) =>
        `${mainConfig.url}/images/ART/${size}/${data.value.code}${suffix}.${e.FORIMG}`,
    );
  };

  const saveEditedImage = (dataUrl: string) => {
    if (editingIndex === null) return;
    addUpdateImage({ index: editingIndex, type: "BASE", value: dataUrl });
    addUpdateImage({ index: editingIndex, type: "FORIMG", value: "png" });
    setEditingIndex(null);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (!files[0].type.startsWith("image/")) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result as string;
      addUpdateImage({
        new: {
          FORIMG: result.split(";")[0].split("/")[1],
          TIPIMG: 0,
          BASE: result,
          DELETE: false,
        },
      });
    };

    reader.readAsDataURL(files[0]);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div>
      <div
        style={{
          padding: 5,
          paddingBottom: 10,
          justifyItems: "center",
          textAlign: "center",
        }}
      >
        <input
          id="fileInput"
          ref={inputRef}
          style={{ display: "none" }}
          type="file"
          accept="image/*,.glb,model/gltf-binary"
          onChange={handleFileChange}
        />
        <label
          htmlFor="fileInput"
          className={"stdButton noSelect"}
          style={{ backgroundColor: colors.green }}
        >
          {t("p.uploadImage")}
        </label>
      </div>
      <div>
        {data.value.images.length > 0 &&
          data.value.images.map((e: stdObject, index: number) => (
            <div
              key={index}
              style={{
                padding: 5,
                width: 150,
                opacity: e.DELETE ? 0.3 : 1,
                border: "solid 2px " + colors.grey,
                borderRadius: 5,
                float: "left",
                justifyItems: "center",
                margin: 3,
              }}
            >
              <div className="listImage">
                <img
                  src={getImageSrc(e)}
                  onError={(e: any) => (e.target.src = NOPIC)}
                  loading="lazy"
                  alt={data.value.code}
                />
              </div>
              <div>
                <MiniLabel
                  title={t("p.type")}
                  text={
                    e.FORIMG === "glb" ? "3D" : e.TIPIMG ? "360°" : t("p.image")
                  }
                />
                <MiniLabel
                  title={t("p.status")}
                  text={
                    e.BASE
                      ? t("p.loaded")
                      : e.DELETE
                        ? t("p.delete")
                        : t("p.saved")
                  }
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  paddingTop: 5,
                  gap: 5,
                }}
              >
                <BasicButton
                  title={"360°"}
                  color={e.TIPIMG ? colors.green : colors.grey}
                  onClick={() =>
                    !e.DELETE &&
                    addUpdateImage({
                      index,
                      type: "TIPIMG",
                      value: e.TIPIMG === 1 ? 0 : 1,
                    })
                  }
                />
                <BasicButton
                  Icon={DeleteForeverIcon}
                  color={colors.red}
                  onClick={() =>
                    addUpdateImage({ index, type: "DELETE", value: !e.DELETE })
                  }
                />
                {e.FORIMG !== "glb" && (
                  <BasicButton
                    Icon={EditIcon}
                    color={colors.blue}
                    onClick={() => !e.DELETE && setEditingIndex(index)}
                  />
                )}
              </div>
            </div>
          ))}
      </div>
      {editingIndex !== null && (
        <ImageEditorModal
          open={editingIndex !== null}
          imageSources={getEditorImageSources(data.value.images[editingIndex])}
          onClose={() => setEditingIndex(null)}
          onSave={saveEditedImage}
        />
      )}
    </div>
  );
}
